import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { analytics } from "@heycatch/sdk";
import { api } from "@/lib/api";
import { syncPush } from "@/lib/push";
import { clearOfflineData } from "@/lib/offline/db";

const AuthContext = createContext(null);

// Wipe the offline read cache/photos when the signed-in account changes, so a
// previous user's cached job list can't flash into a new account on the same device.
async function switchAccount(newId) {
  const prev = localStorage.getItem("sp_uid") || "";
  if (prev && prev !== newId) await clearOfflineData();
  if (newId) localStorage.setItem("sp_uid", newId);
}

function identify(u) {
  if (!u?.id) return;
  analytics.setIdentity(
    u.id,
    { email: u.email, name: u.full_name || "", plan: u.subscription?.status || "none" },
  );
}

// Remember the last signed-in user so a warm launch paints instantly instead of
// blanking on a splash while /auth/me revalidates (which competes with offline sync).
function cacheUser(u) {
  try { u ? localStorage.setItem("sp_user", JSON.stringify(u)) : localStorage.removeItem("sp_user"); } catch { /* ignore */ }
}
function readCachedUser() {
  try {
    if (!localStorage.getItem("sp_token")) return null;
    return JSON.parse(localStorage.getItem("sp_user") || "null");
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const bootUser = typeof window !== "undefined" ? readCachedUser() : null;
  const [user, setUser] = useState(bootUser);
  const [loading, setLoading] = useState(!bootUser);
  const processed = useRef(false);

  const refresh = useCallback(async () => {
    try {
      // Live call via the raw client — auth state must never be served from the
      // offline read-through cache (and must not hang boot if that layer stalls).
      const { data } = await api.raw.get("/auth/me");
      setUser(data);
      cacheUser(data);
      if (data) { identify(data); syncPush(); } // silent: keep the push subscription fresh each launch
      return data;
    } catch (e) {
      // Only sign out on an explicit auth rejection; keep the cached session on
      // network hiccups so a flaky reopen doesn't bounce the user to login.
      if (e?.response?.status === 401) { setUser(null); cacheUser(null); }
      return null;
    }
  }, []);

  useEffect(() => {
    // Safety net: never leave the app wedged on the splash screen if the boot
    // /auth/me call stalls (flaky network or a wedged offline/service-worker layer).
    const t = setTimeout(() => setLoading(false), 7000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const run = async () => {
      const hash = window.location.hash || "";
      if (hash.includes("session_id=")) {
        if (processed.current) return;
        processed.current = true;
        const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
        try {
          let data;
          try {
            ({ data } = await api.post("/auth/google", { session_id: sid }));
          } catch (e) {
            if (e?.response?.status === 409) {
              ({ data } = await api.post("/auth/google", { session_id: sid, force: true }));
            } else { throw e; }
          }
          if (data.token) localStorage.setItem("sp_token", data.token);
          localStorage.setItem("sp_returning", "1");
          if (data.user) await switchAccount(data.user.id);
          setUser(data.user);
          cacheUser(data.user);
          if (data.user) { identify(data.user); syncPush(); }
        } catch {
          /* ignore */
        }
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        setLoading(false);
        return;
      }
      await refresh();
      setLoading(false);
    };
    run();
  }, [refresh]);

  const login = async (email, password, force = false) => {
    const { data } = await api.post("/auth/login", { email, password, force });
    if (data.token) localStorage.setItem("sp_token", data.token);
    localStorage.setItem("sp_returning", "1");
    if (data.user) await switchAccount(data.user.id);
    setUser(data.user);
    cacheUser(data.user);
    if (data.user) { identify(data.user); syncPush(); }
    return data.user;
  };

  const signup = async (email, password, full_name) => {
    let attr = {};
    try { attr = JSON.parse(localStorage.getItem("sp_attr") || "{}") || {}; } catch { /* ignore */ }
    const { data } = await api.post("/auth/signup", {
      email, password, full_name,
      utm_source: attr.utm_source || "", utm_medium: attr.utm_medium || "",
      utm_campaign: attr.utm_campaign || "", referrer: attr.referrer || "",
    });
    if (data.token) localStorage.setItem("sp_token", data.token);
    localStorage.setItem("sp_returning", "1");
    if (data.user) await switchAccount(data.user.id);
    setUser(data.user);
    cacheUser(data.user);
    if (data.user) { identify(data.user); analytics.trackEvent("signup_completed"); }
    return data.user;
  };

  const tryFree = async () => {
    const { data } = await api.post("/auth/anon", {});
    if (data.token) localStorage.setItem("sp_token", data.token);
    localStorage.setItem("sp_returning", "1");
    if (data.user) await switchAccount(data.user.id);
    setUser(data.user);
    cacheUser(data.user);
    if (data.user) { identify(data.user); analytics.trackEvent("try_free_started"); }
    return data.user;
  };

  const convert = async (email, password, full_name) => {
    const { data } = await api.post("/auth/convert", { email, password, full_name });
    if (data.token) localStorage.setItem("sp_token", data.token);
    if (data.user) await switchAccount(data.user.id); // same id — no cache wipe
    setUser(data.user);
    cacheUser(data.user);
    if (data.user) { identify(data.user); analytics.trackEvent("account_created_from_sandbox"); }
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("sp_token");
    localStorage.removeItem("sp_uid");
    localStorage.removeItem("sp_user");
    await clearOfflineData();
    setUser(null);
    analytics.resetIdentity();
  };

  const value = { user, loading, refresh, login, signup, logout, setUser, tryFree, convert };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
